<script setup lang="ts">
import { UiHeader, UiLogo, UiTabsLayout, UiTabsLayoutItem, UiBottomBar, UiButton, UiIcon, UiDialog, UiText, UiToggleButton, type ShopexpertsService, useToaster, UiTooltip, UiBackButton } from "@shopexperts/shared";
import { ref, computed, watch, useTemplateRef } from "vue";
import { useRouter } from "vue-router";
import ListingEditorPackagedServices from "./ListingEditorPackagedServices.vue";
import ListingEditorFAQ from "./ListingEditorFaq.vue";
import ListingEditorStories from "./ListingEditorStories.vue";
import ListingEditorDetails from "./ListingEditorDetails.vue";
import { useListingCoordinator } from "./composables/useListingCoordinator";
import { useListingDetailsStore } from "./composables/useListingDetailsStore";
import { useListingPackagedServicesStore } from "./composables/useListingPackagedServicesStore";
import { useListingCustomerStoriesStore } from "./composables/useListingCustomerStoriesStore";
import { useListingFaqsStore } from "./composables/useListingFaqsStore";
import { useListingPublisher } from "./composables/useListingPublisher";
import { useProfileStatus } from "./composables/useProfileStatus";
import ListingEditorPreview from "./ListingEditorPreview.vue";

const isMobileSelectorOpen = ref(false);
const router = useRouter();
const toaster = useToaster();

const { isDraft, isLoading: isLoadingProfileStatus } = useProfileStatus();

const coordinator = useListingCoordinator();
const { changesList, isValidToPublish, markAllSaved, resetAll, getPublishPayload, getPreviewData } = coordinator;

const detailsStore = useListingDetailsStore();
const packagedStore = useListingPackagedServicesStore();
const storiesStore = useListingCustomerStoriesStore();
const faqsStore = useListingFaqsStore();

const { publish, publishProfile, isPublishingProfile } = useListingPublisher({
  onBeforeInvalidate: markAllSaved,
  getPublishPayload
});

const isSaveDialogOpen = ref(false);
const isCloseDialogOpen = ref(false);
const isPublishProfileDialogOpen = ref(false);
const hasChanges = computed(() => changesList.value.length > 0);
const isPreviewMode = ref(false);
const activeTab = ref('personal');
const detailsFormRef = useTemplateRef('detailsFormRef');
const cachedIsPreviewMode = ref(false);

watch(isMobileSelectorOpen, (val) => {
  if (val && isPreviewMode.value) {
    isPreviewMode.value = false;
    cachedIsPreviewMode.value = true;
  }

  if (!val && cachedIsPreviewMode.value) {
    isPreviewMode.value = true;
    cachedIsPreviewMode.value = false;
  }
});

function onSaveUpdates() {
  if (!hasChanges.value) return;
  
  if (!isValidToPublish.value) {
    isPreviewMode.value = false;
    activeTab.value = 'personal';
    detailsFormRef.value?.validate();
    toaster.error({
      title: "Invalid form data",
      description: "Please fix the invalid fields in Personal & Business Details before saving."
    });
    return;
  }
  
  isSaveDialogOpen.value = true;
}

function onClose() {
  if (hasChanges.value) {
    isCloseDialogOpen.value = true;
  } else {
    router.push('/expert/dashboard');
  }
}

const previewContent = ref<ShopexpertsService>();

// Computed body for preview
const body = computed(() => {
  const previewData = getPreviewData();
  if (previewData) {
    return previewData;
  }
  return previewContent.value;
});

watch([detailsStore.model, packagedStore.model, storiesStore.model, faqsStore.model], () => {
  const previewData = getPreviewData();
  if (previewData) {
    previewContent.value = previewData;
  }
}, { deep: false });

async function onPublishUpdates() {
  if (!body.value) return;

  isSaveDialogOpen.value = false;
  await publish();
}

function onDiscardUpdates() {
  resetAll();
  isSaveDialogOpen.value = false;
  toaster.success({
    title: "Changes discarded",
    description: "Your updates have been discarded"
  });
}

async function onSaveAndClose() {
  if (!body.value) return;
  isCloseDialogOpen.value = false;
  await publish();
  router.push('/expert/dashboard');
}

function onDiscardAndClose() {
  resetAll();
  isCloseDialogOpen.value = false;
  router.push('/expert/dashboard');
}

function onPublishProfileClick() {
  isPublishProfileDialogOpen.value = true;
}

async function onConfirmPublishProfile() {
  isPublishProfileDialogOpen.value = false;
  await publishProfile();
}

const saveButtonLabel = computed(() => isDraft.value ? "Save" : "Save");

const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE;
</script>
<template>
  <div class="min-h-dvh">
    <UiHeader size="md" variant="sticky" no-container>
      <template #main>
        <div class="relative">
          <UiLogo size="auto" logo-class="w-[153.9px]" />
          <UiText as="span" variant="caption-normal" class="absolute bottom-0 right-4 translate-y-4/5 italic"
            text="listing builder" />
        </div>
      </template>
      <template #actions>
        <div class="flex gap-3" v-if="!isMobileSelectorOpen">
          <UiTooltip content="Preview Profile">
            <UiToggleButton variant="outline" v-model="isPreviewMode"
              :label="isPreviewMode ? 'Close Preview' : 'Preview'" disable-active-classes
              :class="isPreviewMode ? 'bg-se-primary border-se-primary' : ''">
              <template #before_label>
                <UiIcon :name="isPreviewMode ? 'close' : 'play'" class="w-5 h-5" />
              </template>
            </UiToggleButton>
          </UiTooltip>
          <div class="hidden md:flex md:gap-3">
            <UiButton variant="outline" :label="saveButtonLabel" @click="onSaveUpdates" :disabled="!hasChanges">
            </UiButton>
            <UiButton v-if="!isLoadingProfileStatus && isDraft" variant="primary" label="Publish" @click="onPublishProfileClick"
              :disabled="hasChanges">
            </UiButton>
          </div>
        </div>
      </template>
    </UiHeader>

    <div v-if="DEBUG_MODE && false"
      class="p-4 bg-gray-100 fixed bottom-0 left-0 right-0 w-full h-80 overflow-y-auto border-t z-100">
      <pre>{{ {
        details: detailsStore.model,
        changesList,
      } }}</pre>
    </div>
    <UiTabsLayout v-show="!isPreviewMode" heading="Manage your listing sections"
      v-model="activeTab" :is-mobile-selector-open="isMobileSelectorOpen" sticky @update:model-value="() => { isMobileSelectorOpen = false; isPreviewMode = false; }">
      <template #before-list>
        <UiBackButton href="#" @click="onClose" label="Back to Dashboard"
          description="Close the editor and return to the main dashboard." />
      </template>
      <UiTabsLayoutItem value="personal" label="Personal & Business Details" icon="user-circle"
        hide-description-unless-selected
        description="Share who you are, your background, and what makes your business unique.">
        <ListingEditorDetails ref="detailsFormRef" />
      </UiTabsLayoutItem>

      <UiTabsLayoutItem value="packages" label="Packaged Services" :locked="true" :disabled="false"
        icon="package-placeholder" hide-description-unless-selected
        description="Present ready-to-go bundles with clear pricing and outcomes for easier client decisions.">
        <ListingEditorPackagedServices :locked="true" />
      </UiTabsLayoutItem>

      <UiTabsLayoutItem value="stories" label="Case Studies" :locked="true" :disabled="false"
        icon="image-placeholder" hide-description-unless-selected
        description="Showcase case studies to build trust and credibility with potential clients.">
        <ListingEditorStories :locked="true" />
      </UiTabsLayoutItem>

      <UiTabsLayoutItem value="faq" label="FAQ" :locked="true" :disabled="false" icon="file-placeholder"
        hide-description-unless-selected
        description="Answer common questions upfront to save time and show transparency in how you work.">
        <ListingEditorFAQ :locked="true" />
      </UiTabsLayoutItem>
    </UiTabsLayout>

    <div v-show="isPreviewMode" class="w-full h-[calc(100dvh-var(--safe-area-top))]">
      <ListingEditorPreview v-if="body" :body="body" />
    </div>

    <UiBottomBar variant="sticky" class="md:hidden">
      <div class="flex justify-between w-full gap-3">
        <UiButton variant="outline" label="Listing Sections" @click="isMobileSelectorOpen = !isMobileSelectorOpen" />
        <Teleport defer to="#header-actions" v-if="isMobileSelectorOpen">
          <UiButton variant="outline" shape="square" class="md:hidden" @click="isMobileSelectorOpen = false">
            <template #before_label>
              <UiIcon name="close" class="w-5 h-5" />
            </template>
          </UiButton>
        </Teleport>
        <div class="flex gap-2">
          <UiButton variant="outline" :label="saveButtonLabel" @click="onSaveUpdates" :disabled="!hasChanges">
          </UiButton>
          <UiButton v-if="!isLoadingProfileStatus && isDraft" variant="primary" label="Publish" @click="onPublishProfileClick"
            :disabled="hasChanges">
          </UiButton>
        </div>
      </div>
    </UiBottomBar>

    <UiDialog v-model="isSaveDialogOpen" title="Publish Updates"
      text="You've made updates to the following sections.&#10;Review them before saving:"
      primary-action-label="Publish Updates" secondary-action-label="Discard Updates" @primary-action="onPublishUpdates"
      @secondary-action="onDiscardUpdates">
      <ul class="list-disc list-inside space-y-1">
        <li v-for="change in changesList" :key="change">
          <UiText as="span" variant="h6" class="font-semibold" :text="change" />
        </li>
      </ul>
    </UiDialog>

    <UiDialog v-model="isCloseDialogOpen" title="Don't lose your updates"
      text="You made some changes to your listing but didn't save them.&#10;To avoid losing updates, go back and save your changes."
      primary-action-label="Save Updates" secondary-action-label="Discard Updates" @primary-action="onSaveAndClose"
      @secondary-action="onDiscardAndClose">
      <ul class="list-disc list-inside space-y-1">
        <li v-for="change in changesList" :key="change">
          <UiText as="span" variant="h6" class="font-semibold" :text="change" />
        </li>
      </ul>
    </UiDialog>

    <UiDialog v-model="isPublishProfileDialogOpen" title="Publish Your Profile"
      text="Once your profile is published, it will be visible to all clients on the platform.&#10;&#10;Please note: Once published, your profile cannot be unpublished. If you need to unlist yourself at any point for any reason, you will need to contact the admins directly."
      primary-action-label="Publish Profile" secondary-action-label="Cancel"
      :primary-action-loading="isPublishingProfile" @primary-action="onConfirmPublishProfile"
      @secondary-action="isPublishProfileDialogOpen = false">
    </UiDialog>
  </div>
</template>
